'use client';

import DOMPurify, { type UponSanitizeAttributeHook } from 'dompurify';
import parse, { Element as DomElement, type DOMNode } from 'html-react-parser';
import React, { JSX, useEffect, useRef, useState } from 'react';

import { Image } from '@/components/atoms';
import { useBreakpoint } from '@/lib/hooks/util/useBreakpoint';

export interface EmbededRendererProps {
  html: string;
  aspectRatio?: string;
  minHeight?: number;
  // Governs <script src> execution (the imperative injector below is the
  // only path that can execute a script — DOMPurify strips <script>
  // unconditionally). Omitted entirely: unrestricted, matching this
  // component's existing behavior for every caller that predates this
  // prop (generic Embed Widget, WysiwygContent, quiz/game pages) — no
  // behavior change for them. Passed as an array: deny-by-default,
  // allowing only origins that match an entry — an empty array denies
  // every script. An entry starting with "." matches that domain and any
  // subdomain (e.g. ".elf.site" matches "abc123.elf.site"), since some
  // providers (Elfsight) serve each embed from a per-instance subdomain
  // rather than a fixed origin. A disallowed script downgrades to the
  // same sandboxed iframe used for a script that fails to load — it is
  // never silently dropped nor silently executed.
  scriptAllowedOrigins?: string[];
  autoResize?: { allowedOrigins: string[] };
}

const DEFAULT_IFRAME_SANDBOX =
  'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms allow-presentation';

const SCRIPT_FALLBACK_TIMEOUT_MS = 3000;

const ALLOWED_ATTRS_PER_TAG: Record<string, string[]> = {
  'MUX-PLAYER': [
    'playback-id',
    'poster',
    'audio',
    'stream-type',
    'metadata-title',
    'metadata-subtitle',
    'auto-play',
    'muted',
  ],
  SCRIPT: ['src'],
  IFRAME: [
    'allow',
    'allowfullscreen',
    'src',
    'width',
    'height',
    'frameborder',
    'title',
    'loading',
    'referrerpolicy',
    'style',
    'sandbox',
    'data-aspect-ratio',
  ],
  IMG: [
    'src',
    'alt',
    'width',
    'height',
    'loading',
    'decoding',
    'sizes',
    'srcset',
  ],
};

const restrictAttributesPerTag: UponSanitizeAttributeHook = (node, data) => {
  const tag = node.nodeName.toUpperCase();
  const allowedAttrs = ALLOWED_ATTRS_PER_TAG[tag] || [];
  if (!allowedAttrs.includes(data.attrName)) {
    data.keepAttr = false;
  }
};

const createFallbackIframe = (src: string): HTMLIFrameElement => {
  const iframe = document.createElement('iframe');
  iframe.src = src;
  iframe.setAttribute('sandbox', DEFAULT_IFRAME_SANDBOX);
  iframe.style.cssText =
    'width: 100%; max-width: 100%; aspect-ratio: 16/9; height: auto; border: 0; display: block;';
  return iframe;
};

export const EmbededRenderer = ({
  html,
  aspectRatio,
  minHeight,
  scriptAllowedOrigins,
}: EmbededRendererProps): JSX.Element => {
  const [sanitizedHtml, setSanitizedHtml] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);
  const isDesktop = useBreakpoint('sm');

  useEffect(() => {
    DOMPurify.addHook('uponSanitizeAttribute', restrictAttributesPerTag);
    const sanitized = DOMPurify.sanitize(html, {
      USE_PROFILES: { html: true },
      ADD_TAGS: ['iframe', 'script', 'mux-player', 'img'],
      ADD_ATTR: [
        'allow',
        'allowfullscreen',
        'frameborder',
        'referrerpolicy',
        'title',
        'style',
        'sandbox',
        'playback-id',
        'poster',
        'audio',
        'stream-type',
        'metadata-title',
        'metadata-subtitle',
        'auto-play',
        'muted',
        'alt',
        'width',
        'height',
        'loading',
        'decoding',
        'sizes',
        'srcset',
        'data-aspect-ratio',
      ],
    });

    setSanitizedHtml(sanitized);

    return () => {
      DOMPurify.removeHook('uponSanitizeAttribute', restrictAttributesPerTag);
    };
  }, [html]);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    // Quote-style and attribute-order agnostic: matches src="..." or
    // src='...' anywhere in the opening tag, with any whitespace between
    // the tags (DOMPurify/backend serialization isn't guaranteed to match
    // the single canonical shape a stricter pattern would require).
    const scriptRegex =
      /<script\b[^>]*\ssrc=(["'])(?<src>[^"']*)\1[^>]*>\s*<\/script\s*>/gi;
    const matches = [...html.matchAll(scriptRegex)];
    const teardownFns: Array<() => void> = [];

    const isOriginAllowed = (src: string): boolean => {
      if (scriptAllowedOrigins === undefined) {
        return true;
      }

      let url: URL;
      try {
        url = new URL(src, window.location.origin);
      } catch {
        return false;
      }

      return scriptAllowedOrigins.some(allowed =>
        allowed.startsWith('.')
          ? url.hostname === allowed.slice(1) || url.hostname.endsWith(allowed)
          : url.origin === allowed,
      );
    };

    matches.forEach(match => {
      const src = match.groups?.src ?? '';
      if (!src) return;

      if (!isOriginAllowed(src)) {
        if (/^https?:\/\//i.test(src)) {
          const iframe = createFallbackIframe(src);
          container.appendChild(iframe);
          teardownFns.push(() => iframe.remove());
        }
        return;
      }

      let settled = false;
      let disposed = false;

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      let currentNode: HTMLScriptElement | HTMLIFrameElement = script;

      const fallbackToIframe = () => {
        if (settled || disposed) return;
        if (!/^https?:\/\//i.test(src)) return;
        settled = true;
        script.remove();

        const iframe = createFallbackIframe(src);
        container.appendChild(iframe);
        currentNode = iframe;
      };

      const handleWindowError = (event: ErrorEvent) => {
        if (disposed) return;
        if (event.filename && event.filename !== src) return;
        if (!event.filename && event.message !== 'Script error.') return;
        fallbackToIframe();
      };

      script.onerror = fallbackToIframe;
      window.addEventListener('error', handleWindowError);

      container.appendChild(script);

      const settleTimer = window.setTimeout(() => {
        settled = true;
        window.removeEventListener('error', handleWindowError);
      }, SCRIPT_FALLBACK_TIMEOUT_MS);

      teardownFns.push(() => {
        disposed = true;
        window.removeEventListener('error', handleWindowError);
        window.clearTimeout(settleTimer);
        currentNode.remove();
      });
    });

    return () => {
      teardownFns.forEach(teardown => teardown());
    };
  }, [html, scriptAllowedOrigins]);

  const parserOptions = {
    replace: (domNode: DOMNode) => {
      if (
        typeof domNode === 'object' &&
        domNode !== null &&
        'name' in domNode
      ) {
        const element = domNode as DomElement;
        const attribs = element.attribs || {};

        // Handle iframe elements
        if (domNode.name === 'iframe') {
          const hasWidth = 'width' in attribs;
          const hasHeight = 'height' in attribs;
          const dataAspectRatio = attribs['data-aspect-ratio'];
          const minHeightStyle = minHeight
            ? ` min-height: ${minHeight}px;`
            : '';

          // Sandbox hardening: embedded content (e.g. Genially quizzes) runs
          // cross-origin — never leave an iframe unsandboxed. Preserve an
          // explicit sandbox value from the source HTML if present, otherwise
          // apply a conservative default.
          if (!attribs.sandbox) {
            attribs.sandbox = DEFAULT_IFRAME_SANDBOX;
          }

          attribs.style = `${attribs.style || ''} max-width: 100%; display: block; margin-inline: auto;${minHeightStyle}`;

          if (hasWidth && hasHeight) {
            attribs.style += ` width: ${attribs.width}; height: ${attribs.height};`;
            return element;
          }

          if (dataAspectRatio) {
            const [wStr, hStr] = dataAspectRatio.split('/');
            const widthRatio = parseFloat(wStr);
            const heightRatio = parseFloat(hStr);

            const isTall =
              widthRatio > 0 && heightRatio > 0 && widthRatio / heightRatio < 1;

            const effectiveAspectRatio =
              isTall && !isDesktop ? '2/3' : dataAspectRatio;

            const maxWidth = isTall ? '540px' : '100%';

            attribs.style += ` width: 100%; aspect-ratio: ${effectiveAspectRatio}; height: auto; max-width: ${maxWidth};`;

            delete attribs.height;

            return element;
          }

          if (hasWidth && !hasHeight) {
            attribs.style += ` width: ${attribs.width};`;
            attribs.style += ' height: auto;';
            attribs.style += ` aspect-ratio: ${aspectRatio || '16/9'};`;
          }

          if (!hasWidth && !hasHeight) {
            attribs.style += ' width: 100%;';
            attribs.style += ' height: auto;';
            attribs.style += ` aspect-ratio: ${aspectRatio || '16/9'};`;
          }

          return element;
        }

        // DOMPurify already strips <script> unconditionally regardless of
        // ADD_TAGS (verified: it never survives sanitize()), which is why
        // script execution is handled exclusively by the imperative effect
        // above operating on the raw, unsanitized html. This is a defense
        // in depth guard in case that DOMPurify behavior ever changes —
        // a script must never reach the declarative render tree, since
        // nothing here would apply the origin allowlist to it.
        if (domNode.name === 'script') {
          return <React.Fragment />;
        }

        // Handle img elements
        if (domNode.name === 'img') {
          const imgWidth = attribs?.width ? Number(attribs.width) : undefined;
          const imgHeight = attribs?.height
            ? Number(attribs.height)
            : undefined;
          return (
            <Image
              src={attribs?.src || ''}
              alt={attribs?.alt || 'Image'}
              wysiwyg={true}
              {...(imgWidth !== undefined && imgHeight !== undefined
                ? { width: imgWidth, height: imgHeight }
                : {})}
            />
          );
        }
      }
    },
  };

  return (
    <div
      ref={containerRef}
      className="w-full max-w-(--layout-dynamic-max-width) flex justify-center h-auto [&>*:has(>iframe)]:w-full"
    >
      {parse(sanitizedHtml, parserOptions)}
    </div>
  );
};
